import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadButton } from "@presentation/components/helpers/UploadButton";
import { RemoteFolderExplorerGrid } from "@presentation/components/helpers/RemoteFolderExplorerGrid";
import { useFastaExplorerStore } from "@presentation/stores/FastaExplorerStore";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { DeleteFastaService, ListFastaService } from "@application/useCases";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useFastaFilesLocationName } from "@presentation/hooks/useStorageLocations";
import { buildRootBreadcrumbLabel } from "@presentation/stores/FileExplorerStore";
import { FastaFile, PathTypeSchema } from "@domain/schemas/BI";
import { UploadFastaDialog } from "@presentation/components/fasta/UploadFastaDialog";

export { buildRowKey } from "@presentation/components/helpers/useRemoteFolderExplorer";

/**
Fasta facet of the shared `RemoteFolderExplorerGrid`/`useRemoteFolderExplorer` core: breadcrumb-based
folder navigation, multi-select with shift-click/select-all-visible/recursive-folder selection, and
`sourcePath` collision disambiguation - but without Raw's `.d`-folder handling, DIA-rerun lock, or
raw/raw_preview/raw_staging candidate fallbacks, none of which apply to Fasta storage.
*/
export const FastaExplorerGrid: React.FC = () => {
  const listFastaService = useInjection(ListFastaService);
  const deleteFastaService = useInjection(DeleteFastaService);
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const { t: loc } = useTranslation();
  const { fastaItems, metaState, selectedState, breadcrumbItems, setFastaItems, setSelectedState, setBreadcrumbItems, setMetaState, resetSelectedGridData, lastProjectId, setLastProjectId } =
    useFastaExplorerStore();
  const setJobFastaFilesInStore = useJobSetupStore((state) => state.setJobFastaFiles);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // The store seeds the root crumb from /config.json so something is shown immediately; the backend is the
  // source of truth (Azure container / AWS folder), so the label is corrected as soon as it answers.
  const fastaFilesLocationName = useFastaFilesLocationName();
  React.useEffect(() => {
    const rootLabel = buildRootBreadcrumbLabel(fastaFilesLocationName);
    const rootItem = breadcrumbItems[0];
    if (!rootItem || rootItem.id !== "home" || rootItem.text === rootLabel) {
      return;
    }

    setBreadcrumbItems(breadcrumbItems.map((item, index) => (index === 0 ? { ...item, text: rootLabel } : item)));
  }, [fastaFilesLocationName, breadcrumbItems, setBreadcrumbItems]);

  return (
    <RemoteFolderExplorerGrid<FastaFile>
      activeProjectId={activeProjectId}
      pathType={PathTypeSchema.Fasta}
      deleteFile={(target) => deleteFastaService.executeAsync(target.projectId, target.fileName, target.folder || undefined, target.sourcePath)}
      selectionMode="multi"
      regexFilterStorageKey="fastaFilesRegex"
      fileNameColumnTitle={loc("fileName")}
      store={{
        items: fastaItems,
        setItems: setFastaItems,
        selectedState,
        setSelectedState,
        breadcrumbItems,
        setBreadcrumbItems,
        resetSelectedGridData,
        lastProjectId,
        setLastProjectId,
        metaState,
        setMetaState,
      }}
      fetchList={(folder, sourcePath) => listFastaService.listFastaAsync(activeProjectId ?? "", sourcePath, folder)}
      toExplorerItem={(fasta) => ({ location: fasta.location, fileName: fasta.fileName, sizeInBytes: fasta.size, sourcePath: fasta.sourcePath })}
      onSelectionChange={(selected) => setJobFastaFilesInStore(selected.map((file) => ({ path: file.path, sizeInBytes: file.sizeInBytes, sourcePath: file.sourcePath ?? null })))}
      toolbarActions={() => <UploadButton onClick={() => setShowUploadDialog(true)} title="Upload Fasta" />}
      dialog={(api) =>
        showUploadDialog && (
          <UploadFastaDialog
            onCancel={() => setShowUploadDialog(false)}
            onUploadSuccess={() => {
              setShowUploadDialog(false);
              void api.refresh();
            }}
          />
        )
      }
    />
  );
};

export default FastaExplorerGrid;
